import { Injectable } from '@angular/core';
import { apiUrl, resolveApiAssets } from '../config/api';
import { Match, MatchGoal, ScorerStanding } from '../models/match';

@Injectable({ providedIn: 'root' })
export class MatchService {
  private readonly apiUrl = apiUrl('/api/matches');

  list(): Promise<Match[]> {
    return this.request<Match[]>(this.apiUrl);
  }

  get(id: number): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}`);
  }

  create(match: Partial<Match>): Promise<Match> {
    return this.request<Match>(this.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(match)
    });
  }

  update(id: number, match: Partial<Match>): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(match)
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<{ success: boolean }>(`${this.apiUrl}/${id}`, { method: 'DELETE' });
  }

  async resetAll(): Promise<void> {
    await this.request<{ success: boolean }>(`${this.apiUrl}/reset-all`, { method: 'PATCH' });
  }

  async deleteGroupMatches(): Promise<void> {
    await this.request<{ success: boolean }>(`${this.apiUrl}/group`, { method: 'DELETE' });
  }

  async deleteKnockoutMatches(): Promise<void> {
    await this.request<{ success: boolean }>(`${this.apiUrl}/knockout`, { method: 'DELETE' });
  }

  setLive(id: number): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}/set-live`, { method: 'PATCH' });
  }

  finish(id: number): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}/finish`, { method: 'PATCH' });
  }

  setScore(id: number, scoreA: number, scoreB: number): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}/score`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score_a: scoreA, score_b: scoreB })
    });
  }

  setGoals(id: number, goals: MatchGoal[]): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}/score`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ goals })
    });
  }

  setShootoutScore(id: number, scoreA: number, scoreB: number): Promise<Match> {
    return this.request<Match>(`${this.apiUrl}/${id}/score`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shootout_score_a: scoreA, shootout_score_b: scoreB })
    });
  }

  live(): Promise<Match | null> {
    return this.request<Match | null>(`${this.apiUrl}/live`);
  }

  scorers(): Promise<ScorerStanding[]> {
    return this.request<ScorerStanding[]>(apiUrl('/api/scorers'));
  }

  private async request<T>(url: string, init?: RequestInit): Promise<T> {
    const response = await fetch(url, { cache: 'no-store', ...init });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.error ?? 'Errore API');
    }

    return resolveApiAssets(data as T);
  }
}
